import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import "./ooraTrafficDataset.css";
import { viewOoraTrafficDataset } from "../../Redux/Actions/ooraTrafficeDatasetActions";
import {
    formatDate,
    formatDateTime,
    formatSlotLabel,
    formatDuration,
    formatDistance,
    getTrafficSegments,
} from "./ooraTrafficDatasetHelpers";

const ViewOoraTrafficDataset = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { ooraTrafficDataset, singleError } = useSelector(
        (state) => state.ooraTrafficDataset || {}
    );

    const dataset = ooraTrafficDataset?.data;
    const ready = dataset?._id === id;

    useEffect(() => {
        dispatch(viewOoraTrafficDataset(id));
    }, [dispatch, id]);

    const segments = ready ? getTrafficSegments(dataset.polyline, dataset.speed_intervals) : [];
    const ratio =
        ready && dataset.static_duration_sec
            ? (dataset.traffic_duration_sec / dataset.static_duration_sec).toFixed(2)
            : "-";

    return (
        <>
            <div className="otd-wrapper">
                <div className="otd-header">
                    <div className="otd-header-left">
                        <div className="otd-header-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="otd-title">{ready ? dataset.road_name : "Traffic Details"}</h1>
                            <p className="otd-subtitle">
                                {ready ? `${dataset.from?.name} → ${dataset.to?.name}` : "Traffic by road, date and departure time"}
                            </p>
                        </div>
                    </div>
                    <div className="otd-actions">
                        <button type="button" className="otd-btn otd-btn--ghost" onClick={() => navigate("/oora-traffic-dataset")}>
                            Back
                        </button>
                    </div>
                </div>

                <div className="otd-card">
                    {!ready ? (
                        <div className="otd-empty" style={{ padding: 48, textAlign: "center" }}>
                            {singleError ? "Traffic record not found" : "Loading..."}
                        </div>
                    ) : (
                        <div className="otd-form-body">
                            <div className="otd-meta">
                                <div>
                                    <span className="otd-label">Date</span>
                                    <p>{formatDate(dataset.date_label)}</p>
                                </div>
                                <div>
                                    <span className="otd-label">Slot (IST)</span>
                                    <p>{formatSlotLabel(dataset.slot)}</p>
                                </div>
                                <div>
                                    <span className="otd-label">Last Fetched</span>
                                    <p>{formatDateTime(dataset.updated_at)}</p>
                                </div>
                            </div>

                            <div className="otd-meta">
                                <div>
                                    <span className="otd-label">From</span>
                                    <p>{dataset.from?.name}</p>
                                    <p className="otd-mono-text">
                                        {dataset.from?.lat}, {dataset.from?.lng}
                                    </p>
                                </div>
                                <div>
                                    <span className="otd-label">To</span>
                                    <p>{dataset.to?.name}</p>
                                    <p className="otd-mono-text">
                                        {dataset.to?.lat}, {dataset.to?.lng}
                                    </p>
                                </div>
                                <div>
                                    <span className="otd-label">Route Distance</span>
                                    <p>{formatDistance(dataset.distance_meters)}</p>
                                </div>
                            </div>

                            <div className="otd-meta">
                                <div>
                                    <span className="otd-label">Route Via</span>
                                    <p>{dataset.route_via || "-"}</p>
                                </div>
                                <div>
                                    <span className="otd-label">Created</span>
                                    <p>{formatDate(dataset.created_at)}</p>
                                </div>
                            </div>

                            <div className="otd-docs-head">
                                <h3>Traffic</h3>
                                <span className={`otd-level otd-level--${dataset.traffic_level}`}>{dataset.traffic_level}</span>
                            </div>

                            <div className="otd-table-wrap otd-inner-table">
                                <table className="otd-table">
                                    <thead>
                                        <tr>
                                            <th>Travel Time</th>
                                            <th>Normal Time</th>
                                            <th>Delay</th>
                                            <th>Ratio</th>
                                            <th>Traffic</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{formatDuration(dataset.traffic_duration_sec)}</td>
                                            <td>{formatDuration(dataset.static_duration_sec)}</td>
                                            <td>{formatDuration(dataset.delay_sec)}</td>
                                            <td className="otd-mono">{ratio}</td>
                                            <td>
                                                <span className={`otd-level otd-level--${dataset.traffic_level}`}>{dataset.traffic_level}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="otd-docs-head">
                                <h3>Slow / jammed stretches</h3>
                                <span className="otd-peak">{segments.length} stretch{segments.length === 1 ? "" : "es"}</span>
                            </div>

                            {segments.length ? (
                                <div className="otd-table-wrap otd-inner-table">
                                    <table className="otd-table">
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Length</th>
                                                <th>Start (lat, lng)</th>
                                                <th>End (lat, lng)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {segments.map((g, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <span className={`otd-level otd-level--${g.speed === "TRAFFIC_JAM" ? "SEVERE" : "HEAVY"}`}>
                                                            {g.speed === "TRAFFIC_JAM" ? "JAM" : "SLOW"}
                                                        </span>
                                                    </td>
                                                    <td>{formatDistance(g.distance_meters)}</td>
                                                    <td className="otd-mono">{g.start?.lat}, {g.start?.lng}</td>
                                                    <td className="otd-mono">{g.end?.lat}, {g.end?.lng}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="otd-hint">No slow or jammed stretch in this slot</p>
                            )}
                        </div>
                    )}
                    <div className="otd-docs-head">
                        <h3>Jammed roads on this route</h3>
                        <span className="otd-peak">{dataset?.jammed_roads?.length || 0} road{dataset?.jammed_roads?.length === 1 ? "" : "s"}</span>
                    </div>

                    {dataset?.jammed_roads?.length ? (
                        <div className="otd-table-wrap otd-inner-table">
                            <table className="otd-table">
                                <thead>
                                    <tr>
                                        <th>Road</th>
                                        <th>Type</th>
                                        <th>Jam length</th>
                                        <th>Start (lat, lng)</th>
                                        <th>End (lat, lng)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dataset.jammed_roads.map((r) => (
                                        <tr key={r.road_place_id}>
                                            <td className="otd-row-title">{r.road_name}</td>
                                            <td>
                                                <span className={`otd-level otd-level--${r.speed === "TRAFFIC_JAM" ? "SEVERE" : "HEAVY"}`}>
                                                    {r.speed === "TRAFFIC_JAM" ? "JAM" : "SLOW"}
                                                </span>
                                            </td>
                                            <td>{formatDistance(r.jam_length_meters)}</td>
                                            <td className="otd-mono">{r.start?.lat}, {r.start?.lng}</td>
                                            <td className="otd-mono">{r.end?.lat}, {r.end?.lng}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="otd-hint">No jammed road found on this route in this slot</p>
                    )}
                </div>
            </div>
        </>
    );
};

export default ViewOoraTrafficDataset;